import i18nFactory from '@yandex-int/i18n';
import { difference, isUndefined } from 'lodash';
import * as React from 'react';
import { ReactNode } from 'react';

import { BetaMark } from '../../components/BetaMark/BetaMark';
import { HoverTooltipWrapper } from '../../components/HoverTooltipWrapper/HoverTooltipWrapper';
import { ShowUnsavedBannersButton } from '../../components/ShowUnsavedBannersButton/ShowUnsavedBannersButton';
import { BannerBulkActionsResponse } from '../../typings/banners';
import {
    banners,
    campaigns,
    feedFilters,
    groups,
    LinkTarget,
    phrases,
    retargetings,
    usersProfile,
    ValidationResultItem
} from '../../typings/common';
import { CampaignsCalcTypeFilter, GridRow, Group, GroupItemActionAccess } from '../../typings/domain';
import {
    ActionResult,
    BannersAction,
    CampaignAction,
    CampaignsAction,
    FeedFiltersAction,
    GridAction,
    GridActionData,
    GridActionGroup,
    GridActionItem,
    GridActionItems,
    GridSelection,
    GridTooltipActionCampaignFlags,
    GridTooltipActionGroupFlags,
    GridViewType,
    GroupsAction,
    NoticeType,
    PhrasesAction,
    RetargetingsAction,
    UsersProfileAction
} from '../../typings/grid';
import { convertServerPathToClient, mapTextToErrors } from '../ErrorsMapper/ErrorsMapper';
import { showNoticeMessage } from '../GridHelpers/GridHelpers';
import {
    getNoticeTypeFromSuccessfulItemsRatioStrict,
    getRequestResultNoticeParams
} from '../NoticeTypeHelper/NoticeTypeHelper';
import {
    organizationBannerErrorMapper,
    organizationBannerErrorPathMapper,
    organizationErrorMapper,
    organizationErrorPathMapper
} from '../OrganizationErrorMappers/OrganizationErrorMappers';

import * as keyset from './gridItemsActionsHelpers.i18n';

const helperI18N = i18nFactory(keyset);

interface ItemsActionGroupsSettings {
    title?: string;
    items: Array<GridAction>;
}

/* Возвращает тип перехода по ссылке для массового действия над списками сущностей */
function getLinkTargetForItemsAction(action: GridAction, view: GridViewType): LinkTarget | undefined {
    const dictionary = {
        [campaigns]: {
            [CampaignsAction.goToStatistics]: LinkTarget.blank
        },
        [groups]: {
            [GroupsAction.goToStatistics]: LinkTarget.blank
        }
    };

    if (dictionary[view]) {
        return dictionary[view][action];
    }

    return undefined;
}

/* Возвращает название для массового действия над списками сущностей */
function getTextForItemsAction(action: GridAction, view: GridViewType): string {
    const dictionary = {
        [campaigns]: {
            [CampaignsAction.resume]: helperI18N('resume'),
            [CampaignsAction.stop]: helperI18N('stop'),
            [CampaignsAction.archive]: helperI18N('archive'),
            [CampaignsAction.unarchive]: helperI18N('unarchive'),
            [CampaignsAction.remoderate]: helperI18N('remoderate'),
            [CampaignsAction.copy]: helperI18N('copy'),
            [CampaignsAction.delete]: helperI18N('delete'),
            [CampaignsAction.goToGroups]: helperI18N('goToGroups'),
            [CampaignsAction.goToBanners]: helperI18N('goToBanners'),
            [CampaignsAction.goToPhrases]: helperI18N('goToPhrases'),
            [CampaignsAction.goToStatistics]: helperI18N('goToMol'),
            [CampaignsAction.goToLogs]: helperI18N('goToLogs'),
            [CampaignsAction.goToCampaignModerationOffer]: helperI18N('moderate'),
            [CampaignsAction.addToFavorites]: helperI18N('addToFavorites'),
            [CampaignsAction.removeFromFavorites]: helperI18N('removeFromFavorites'),
            [CampaignsAction.payByCash]: helperI18N('payByCash'),
            [CampaignsAction.exportToExcelWithBanners]: helperI18N('export-to-excel-with-banners'),
            [CampaignsAction.exportToExcelWithoutBanners]: helperI18N('export-to-excel-without-banners')
        },
        [groups]: {
            [GroupsAction.resume]: helperI18N('resume'),
            [GroupsAction.stop]: helperI18N('stop'),
            [GroupsAction.delete]: helperI18N('delete'),
            [GroupsAction.sendBK]: helperI18N('sendBK'),
            [GroupsAction.archive]: helperI18N('archive'),
            [GroupsAction.copy]: helperI18N('copy'),
            [GroupsAction.unarchive]: helperI18N('unarchive'),
            [GroupsAction.remoderateBannersAdditions]: helperI18N('remoderateBannersAdditions'),
            [GroupsAction.moderateAcceptBannersAdditions]: helperI18N('moderateAcceptBannersAdditions'),
            [GroupsAction.goToBanners]: helperI18N('goToBanners'),
            [GroupsAction.goToPhrases]: helperI18N('goToPhrases'),
            [GroupsAction.goToTargeting]: helperI18N('goToTargeting'),
            [GroupsAction.sendModerateByAgency]: helperI18N('sendModerate'),
            [GroupsAction.sendModerateByClient]: helperI18N('sendModerateByClient'),
            [GroupsAction.admSendMD]: helperI18N('admSendMD'),
            [GroupsAction.moderateAccept]: helperI18N('moderateAccept'),
            [GroupsAction.editGroups]: [helperI18N('editGroups')],
            [GroupsAction.openMinusPhraseSelector]: helperI18N('openMinusPhraseSelector'),
            [GroupsAction.regionsTransform]: helperI18N('regions-transform'),
            [GroupsAction.goToStatistics]: helperI18N('goToMol'),
            [GroupsAction.openPhrasesSelectorWizard]: helperI18N('openPhrasesSelectorWizard'),
            [GroupsAction.openPhrasesSelector]: [
                helperI18N('openPhrasesSelectorWizard'),
                <BetaMark key="beta-mark-key" />
            ],
            [GroupsAction.exportToExcelWithBanners]: helperI18N('export-to-excel-with-banners'),
            [GroupsAction.exportToExcelWithoutBanners]: helperI18N('export-to-excel-without-banners')
        },
        [banners]: {
            [BannersAction.stop]: helperI18N('stopBanner'),
            [BannersAction.resume]: helperI18N('resumeBanner'),
            [BannersAction.archive]: helperI18N('archiveBanner'),
            [BannersAction.unarchive]: helperI18N('unarchiveBanner'),
            [BannersAction.delete]: helperI18N('deleteBanners'),
            [BannersAction.editBanners]: [helperI18N('editBannersMass')],
            [BannersAction.editBannersInternal]: [helperI18N('editBannersMass')],
            [BannersAction.editPrices]: [helperI18N('editPrices')],
            [BannersAction.editYellowPagesData]: helperI18N('edit-organization'),
            [BannersAction.exportToExcel]: helperI18N('export-to-excel')
        },
        [phrases]: {
            [PhrasesAction.delete]: helperI18N('delete-phrases'),
            [PhrasesAction.enable]: helperI18N('enable-phrases'),
            [PhrasesAction.openSearchAndReplaceInspector]: helperI18N('open-search-and-replace-inspector'),
            [PhrasesAction.disable]: helperI18N('disable-phrases'),
            [PhrasesAction.pricesWizard]: helperI18N('pricesWizard'),
            [PhrasesAction.setPhrasesCommonPriceEditor]: helperI18N('set-common-price')
        },
        [feedFilters]: {
            [FeedFiltersAction.edit]: helperI18N('edit-feed-filters'),
            [FeedFiltersAction.delete]: helperI18N('delete-feed-filters'),
            [FeedFiltersAction.enable]: helperI18N('enable-feed-filters'),
            [FeedFiltersAction.disable]: helperI18N('disable-feed-filters'),
            [FeedFiltersAction.changeRate]: helperI18N('change-feed-filter-rate'),
            [FeedFiltersAction.changeAutobudget]: helperI18N('change-feed-filter-autobudget-priority')
        },
        [retargetings]: {
            [RetargetingsAction.resume]: helperI18N('resume-retargetings'),
            [RetargetingsAction.suspend]: helperI18N('suspend-retargetings'),
            [RetargetingsAction.setRetargetingCommonPriceEditor]: helperI18N('set-common-price'),
            [RetargetingsAction.delete]: helperI18N('delete-retargetings')
        },
        [usersProfile]: {
            [UsersProfileAction.setUsersProfileCommonPriceEditor]: helperI18N('set-common-price')
        }
    };
    if (!dictionary[view] || !dictionary[view][action]) {
        throw new Error(`Для типа ${view} действия ${action} не определено название`);
    }

    return dictionary[view][action];
}

/* Возвращает текст для тултипа */
export function getTextForItemsCampaignsActionTooltipText(
    actionData: GridTooltipActionCampaignFlags
): string | undefined {
    switch (actionData.action) {
        case CampaignAction.regionsTransform: {
            const { isCampaignEditDisabled, accessForEditRegions } = actionData;

            if (!accessForEditRegions) {
                return helperI18N('regions-transform-access-error');
            }

            if (isCampaignEditDisabled) {
                return helperI18N('no-editable');
            }

            return undefined;
        }
        default:
            return undefined;
    }
}

/* Возвращает текст для тултипа */
export function getTextForItemsGroupsActionTooltipText(actionData: GridTooltipActionGroupFlags): string | undefined {
    switch (actionData.action) {
        case GroupsAction.delete: {
            const { isOverMaxSelection } = actionData;

            if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-delete');
            }

            return undefined;
        }
        case GroupsAction.sendBK: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-sendBK');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-sendBK');
            }

            return undefined;
        }

        case GroupsAction.openMinusPhraseSelector: {
            const {
                isOverMaxSelection,
                canSelectedCampaignTypesManageLibraryMinusPhrase,
                maxSelectionLength,
                canManageLibraryMinusPhrase
            } = actionData;

            if (!canManageLibraryMinusPhrase) {
                return helperI18N('openMinusPhraseSelectorNoRights');
            } else if (!canSelectedCampaignTypesManageLibraryMinusPhrase) {
                return helperI18N('openMinusPhraseSelectorDisabledInfo');
            } else if (isOverMaxSelection) {
                return helperI18N('openMinusPhraseSelectorOverMaxInfo', {
                    count: maxSelectionLength
                });
            }

            return undefined;
        }
        case GroupsAction.moderateAcceptBannersAdditions: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-be-accept-moderation-additions');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-accept-moderation-additions');
            }

            return undefined;
        }
        case GroupsAction.remoderateBannersAdditions: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-be-remoderate-additions');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-remoderate-additions');
            }

            return undefined;
        }
        case GroupsAction.admSendMD: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-be-adm-send-md');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-adm-send-md');
            }

            return undefined;
        }
        case GroupsAction.sendModerateByAgency:
        case GroupsAction.sendModerateByClient: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-be-send-moderate');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-send-moderate');
            }

            return undefined;
        }
        case GroupsAction.moderateAccept: {
            const { isOverMaxSelection, isSelectionValid } = actionData;

            if (!isSelectionValid) {
                return helperI18N('group-can-not-be-moderate-accept');
            } else if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-moderate-accept');
            }

            return undefined;
        }
        case GroupsAction.copy: {
            const { isOverMaxSelection, canCopyGroups, calcTypeFilterValue, canCopyCpmGroups } = actionData;

            const mapperError = {
                [CampaignsCalcTypeFilter.CPC]: {
                    text: helperI18N('copy-disabled-info'),
                    value: canCopyGroups
                },
                [CampaignsCalcTypeFilter.CPM]: {
                    text: helperI18N('copy-disabled-info-cpm'),
                    value: canCopyCpmGroups
                }
            };
            const canCampaignCopy = !mapperError[calcTypeFilterValue].value;

            if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-copy');
            }

            if (canCampaignCopy) {
                return mapperError[calcTypeFilterValue].text;
            }

            return undefined;
        }
        case GroupsAction.regionsTransform: {
            const { isOverMaxSelection, canEditRegions, accessForEditRegions } = actionData;

            if (!accessForEditRegions) {
                return helperI18N('regions-transform-access-error');
            }

            if (!canEditRegions) {
                return helperI18N('group-can-not-regions-transform');
            }

            if (isOverMaxSelection) {
                return helperI18N('group-exceeded-max-size-to-regions-transform');
            }

            return undefined;
        }
        default:
            return undefined;
    }
}

// Возвращает сообщение для конферма
export const getTextForRowItemActionConfirm = (action: GridAction, name: string): string => {
    const dictionary = {
        [GroupsAction.delete]: helperI18N('delete-confirm-single-groups', {
            name
        }),
        [GroupsAction.sendBK]: helperI18N('sendBK-confirm-single-groups', {
            name
        }),
        [GroupsAction.moderateAccept]: helperI18N('accept-moderations-single-confirm-groups', {
            name
        }),
        [GroupsAction.moderateAcceptBannersAdditions]: helperI18N('accept-additions-single-confirm-groups', {
            name
        })
    };

    if (!dictionary[action]) {
        throw new Error(`Для действия ${action} не определен текст для конферма`);
    }

    return dictionary[action];
};

export const getTextForItemActionConfirm = (action: GridAction): string => {
    const dictionary = {
        [CampaignsAction.delete]: helperI18N('delete-confirm-campaigns'),
        [GroupsAction.delete]: helperI18N('delete-confirm-groups'),
        [GroupsAction.moderateAccept]: helperI18N('accept-moderations-confirm-groups'),
        [GroupsAction.moderateAcceptBannersAdditions]: helperI18N('accept-additions-confirm-groups'),
        [GroupsAction.sendBK]: helperI18N('sendBK-confirm-groups'),
        [BannersAction.delete]: helperI18N('delete-confirm-banners'),
        [PhrasesAction.delete]: helperI18N('delete-confirm-phrases'),
        [FeedFiltersAction.delete]: helperI18N('delete-confirm-feed-filters'),
        [RetargetingsAction.delete]: helperI18N('delete-confirm-retargetings')
    };

    if (!dictionary[action]) {
        throw new Error(`Для действия ${action} не определен текст для конферма`);
    }

    return dictionary[action];
};

const gridItemsActionGroups = {
    [campaigns]: [
        {
            title: helperI18N('status-title'),
            items: [
                CampaignsAction.resume,
                CampaignsAction.stop,
                CampaignsAction.archive,
                CampaignsAction.unarchive,
                CampaignsAction.remoderate,
                CampaignsAction.addToFavorites,
                CampaignsAction.removeFromFavorites,
                CampaignsAction.payByCash
            ]
        },
        {
            title: helperI18N('actions-title'),
            items: [
                CampaignsAction.copy,
                CampaignsAction.goToCampaignModerationOffer,
                CampaignsAction.exportToExcelWithBanners,
                CampaignsAction.exportToExcelWithoutBanners,
                CampaignsAction.delete
            ]
        },
        {
            items: [CampaignsAction.goToStatistics, CampaignsAction.goToLogs]
        }
    ],
    [groups]: [
        {
            title: helperI18N('status-title'),
            items: [GroupsAction.resume, GroupsAction.stop, GroupsAction.archive, GroupsAction.unarchive]
        },
        {
            title: helperI18N('actions-title'),
            items: [
                GroupsAction.editGroups,
                GroupsAction.copy,
                GroupsAction.sendModerateByClient,
                GroupsAction.sendModerateByAgency,
                GroupsAction.exportToExcelWithBanners,
                GroupsAction.exportToExcelWithoutBanners,
                GroupsAction.delete
            ]
        },
        {
            title: helperI18N('tool-title'),
            items: [
                GroupsAction.goToStatistics,
                GroupsAction.openPhrasesSelectorWizard,
                GroupsAction.openPhrasesSelector,
                GroupsAction.openMinusPhraseSelector,
                GroupsAction.regionsTransform
            ]
        },
        {
            title: helperI18N('internal-tool-title'),
            items: [
                GroupsAction.remoderateBannersAdditions,
                GroupsAction.moderateAcceptBannersAdditions,
                GroupsAction.moderateAccept,
                GroupsAction.admSendMD,
                GroupsAction.sendBK
            ]
        }
    ],
    [phrases]: [
        {
            title: helperI18N('status-title'),
            items: [
                PhrasesAction.enable,
                PhrasesAction.disable,
                PhrasesAction.delete,
                PhrasesAction.openSearchAndReplaceInspector
            ]
        },
        {
            title: helperI18N('actions-title'),
            items: [PhrasesAction.pricesWizard, PhrasesAction.setPhrasesCommonPriceEditor]
        }
    ],
    [banners]: [
        {
            title: helperI18N('status-title'),
            items: [BannersAction.stop, BannersAction.resume, BannersAction.archive, BannersAction.unarchive]
        },
        {
            title: helperI18N('actions-title'),
            items: [
                BannersAction.editBanners,
                BannersAction.editBannersInternal,
                BannersAction.delete,
                BannersAction.editPrices,
                BannersAction.editYellowPagesData,
                BannersAction.exportToExcel
            ]
        }
    ],
    [feedFilters]: [
        {
            items: [
                FeedFiltersAction.changeRate,
                FeedFiltersAction.changeAutobudget,
                FeedFiltersAction.edit,
                FeedFiltersAction.enable,
                FeedFiltersAction.disable,
                FeedFiltersAction.delete
            ]
        }
    ],
    [retargetings]: [
        {
            title: helperI18N('status-title'),
            items: [RetargetingsAction.resume, RetargetingsAction.suspend]
        },
        {
            title: helperI18N('actions-title'),
            items: [RetargetingsAction.setRetargetingCommonPriceEditor, RetargetingsAction.delete]
        }
    ],
    [usersProfile]: [
        {
            title: helperI18N('actions-title'),
            items: [UsersProfileAction.setUsersProfileCommonPriceEditor]
        }
    ]
};

function getTooltipMenuItem(tooltipText: string, text: string): React.ReactNode {
    return (
        <HoverTooltipWrapper tooltipText={tooltipText} to={['right']}>
            {text}
        </HoverTooltipWrapper>
    );
}

/* Возвращает элемент списка меню для действия */
function getActionsMenuItem(actionData: GridActionData, view: GridViewType): GridActionItem {
    const { action, type, tooltipText, disabled = false, hidden = false } = actionData;

    const text =
        disabled && tooltipText !== undefined
            ? getTooltipMenuItem(tooltipText, getTextForItemsAction(action, view))
            : getTextForItemsAction(action, view);
    const linkTarget = getLinkTargetForItemsAction(action, view);

    return {
        type,
        val: action,
        disabled,
        hidden,
        text,
        linkTarget
    };
}

function getActions(enabledActionsList: Array<GridActionData>, view: GridViewType): GridActionItems {
    const actionsList = [...enabledActionsList];
    const gridItemsActionGroupsForView: Array<ItemsActionGroupsSettings> = gridItemsActionGroups[view];

    return gridItemsActionGroupsForView.reduce(
        (res: GridActionItems, group: ItemsActionGroupsSettings) => {
            const groupActions = group.items.reduce(
                (resultActions: Array<GridActionItem>, groupItem: GridAction) => {
                    const actionData = actionsList.find(el => el.action === groupItem);

                    if (actionData && !actionData.hidden) {
                        resultActions.push(getActionsMenuItem(actionData, view));
                    }

                    return resultActions;
                },
                [] as Array<GridActionItem>
            );

            if (groupActions.length !== 0) {
                res.push({
                    title: group.title,
                    items: groupActions
                });
            }

            return res;
        },
        [] as GridActionItems
    );
}

/**
 *  Возвращает объект для формирования меню выпадающих действий списка таблицы
 *  в соответствии с переданным списком доступных действий.
 */
export function getGridItemsActions(actionsList: Array<GridActionData>, view: GridViewType): Array<GridActionGroup> {
    return getActions(actionsList, view);
}

/* Возвращает сообщение превышении количества элементов для массовых действий */
export function getUniversalActionMessageMaxLimit(sourceParamsView: GridViewType, count: number): string | null {
    switch (sourceParamsView) {
        case phrases:
            return helperI18N('phrases-universal-with-yt-limit', { count });
        case banners:
            return helperI18N('banners-universal-with-yt-limit', { count });
        case groups:
            return helperI18N('groups-universal-with-yt-limit', { count });
        case campaigns:
            return helperI18N('campaigns-universal-with-yt-limit', { count });
        case feedFilters:
            return helperI18N('feed-filters-universal-with-yt-limit', {
                count
            });
        case 'retargetings':
            return helperI18N('retargetings-universal-with-yt-limit', {
                count
            });
        case usersProfile:
            return helperI18N('users-profile-universal-with-yt-limit', {
                count
            });
        default:
            throw Error('для sourceParamsView не принадлежит ни одному типу GridViewType');
    }
}

export const showCampaignBulkActionMessage = (bulkAction: GridAction, params: ActionResult): void => {
    const message = getCampaignsMassActionsResultMessage(bulkAction, params.noticeType);

    if (message) {
        showNoticeMessage(params.noticeType, message);
    }
};

export function getCampaignsMassActionsResultMessage(action: GridAction, noticeType: NoticeType): string {
    const dictionary = {
        [`${CampaignsAction.archive}-success`]: helperI18N('archive-success'),

        [`${CampaignsAction.copy}-success`]: helperI18N('copy-success'),
        [`${CampaignsAction.copy}-error`]: helperI18N('copy-error'),

        [`${CampaignsAction.delete}-success`]: helperI18N('campaigns-delete-success'),
        [`${CampaignsAction.remoderate}-success`]: helperI18N('remoderate-success'),
        [`${CampaignsAction.resume}-success`]: helperI18N('resume-success'),
        [`${CampaignsAction.stop}-success`]: helperI18N('stop-success'),
        [`${CampaignsAction.unarchive}-success`]: helperI18N('unarchive-success'),
        [`${CampaignsAction.addToFavorites}-success`]: helperI18N('campaigns-added-to-favorites'),
        [`${CampaignsAction.removeFromFavorites}-success`]: helperI18N('campaigns-removed-from-favorites')
    };
    return dictionary[`${action}-${noticeType}`];
}

export function getPhrasesMassActionsResultMessage(action: GridAction): string {
    const dictionary = {
        [PhrasesAction.delete]: helperI18N('phrases-delete-success'),
        [PhrasesAction.enable]: helperI18N('phrases-enable-success'),
        [PhrasesAction.disable]: helperI18N('phrases-disable-success')
    };

    return dictionary[action];
}

export function showPhrasesActionNoticeMessage(action: PhrasesAction, successCount: number, totalCount: number): void {
    const { noticeType, noticeMessageType } = getRequestResultNoticeParams(totalCount, successCount);
    const message = getPhrasesMassSetCommonPriceActionsResultMessage(
        action,
        successCount,
        totalCount,
        noticeMessageType
    );

    showNoticeMessage(noticeType, message);
}

export function getPhrasesMassSetCommonPriceActionsResultMessage(
    action: PhrasesAction,
    successCount: number,
    totalCount: number,
    noticeType: NoticeType
): string {
    const isMassAction = totalCount !== 1;

    const dictionary = {
        [`${PhrasesAction.setPhrasesCommonPriceEditor}-${NoticeType.success}`]: isMassAction
            ? helperI18N('phrases-set-common-price-success-all')
            : helperI18N('phrases-set-common-price-success-one'),
        [`${PhrasesAction.setPhrasesCommonPriceEditor}-${NoticeType.warning}`]: helperI18N(
            'phrases-set-common-price-warning',
            {
                count: successCount,
                totalCount
            }
        ),
        [`${PhrasesAction.setPhrasesCommonPriceEditor}-${NoticeType.error}`]: getGridActionErrorMessage(
            PhrasesAction.setPhrasesCommonPriceEditor,
            isMassAction
        )
    };

    return dictionary[`${action}-${noticeType}`];
}

export function getGroupItemActionsResultMessage(action: GridAction, noticeType: NoticeType, name: string): string {
    const dictionary = {
        [`${GroupsAction.remoderateBannersAdditions}-success`]: helperI18N(
            'group-single-remoderate-banners-additions-success',
            {
                name
            }
        ),
        [`${GroupsAction.remoderateBannersAdditions}-error`]: helperI18N(
            'group-single-remoderate-banners-additions-error',
            {
                name
            }
        ),
        [`${GroupsAction.moderateAcceptBannersAdditions}-success`]: helperI18N(
            'group-single-moderate-accept-banners-additions-success',
            {
                name
            }
        ),
        [`${GroupsAction.moderateAcceptBannersAdditions}-error`]: helperI18N(
            'group-single-moderate-accept-banners-additions-error',
            {
                name
            }
        ),
        [`${GroupsAction.delete}-success`]: helperI18N('group-single-delete-success', {
            name
        }),
        [`${GroupsAction.delete}-error`]: helperI18N('group-single-delete-error', {
            name
        }),
        [`${GroupsAction.sendBK}-success`]: helperI18N('group-single-sendBK-success', {
            name
        }),
        [`${GroupsAction.sendBK}-error`]: helperI18N('group-single-sendBK-error', {
            name
        }),
        [`${GroupsAction.admSendMD}-success`]: helperI18N('group-single-adm-send-md-success', {
            name
        }),
        [`${GroupsAction.admSendMD}-error`]: helperI18N('group-single-adm-send-md-error', {
            name
        }),
        [`${GroupsAction.moderateAccept}-success`]: helperI18N('group-single-moderate-accept-success', {
            name
        }),
        [`${GroupsAction.moderateAccept}-error`]: helperI18N('group-single-moderate-accept-error', {
            name
        }),
        [`${GroupsAction.sendModerateByAgency}-success`]: helperI18N('group-single-send-moderate-success', {
            name
        }),
        [`${GroupsAction.sendModerateByAgency}-error`]: helperI18N('group-single-send-moderate-error', {
            name
        }),
        [`${GroupsAction.sendModerateByClient}-success`]: helperI18N('group-single-send-moderate-success'),
        [`${GroupsAction.sendModerateByClient}-error`]: helperI18N('group-single-send-moderate-error')
    };

    return dictionary[`${action}-${noticeType}`];
}
export const showGroupBulkActionMessage = (bulkAction: GridAction, params: ActionResult): void => {
    const message = getGroupsMassActionsResultMessage(
        bulkAction,
        params.successCount,
        params.skippedCount,
        params.noticeType
    );

    showNoticeMessage(params.noticeType, message);
};

export const showGroupBulkActionAltMessage = (
    bulkAction: GridAction,
    groupIds: GridSelection,
    successCount: number,
    skippedCount: number
): void => {
    const { noticeType, noticeMessageType } = getRequestResultNoticeParams(groupIds.length, successCount);

    const message = getGroupsMassActionsResultMessage(bulkAction, successCount, skippedCount, noticeMessageType);

    showNoticeMessage(noticeType, message);
};

export function getGroupsMassActionsResultMessage(
    action: GridAction,
    count: number,
    skippedCount: number,
    noticeType: NoticeType
): string {
    const allCount = skippedCount + count;

    const dictionary = {
        [`${GroupsAction.archive}-success`]: helperI18N('group-archive-success'),
        [`${GroupsAction.unarchive}-success`]: helperI18N('group-unarchive-success'),
        [`${GroupsAction.resume}-success`]: helperI18N('group-resume-success'),
        [`${GroupsAction.stop}-success`]: helperI18N('group-stop-success'),
        [`${GroupsAction.stop}-error`]: helperI18N('group-stop-success-none'),
        [`${GroupsAction.archive}-warning`]: helperI18N('group-archive-success-some', {
            count,
            allCount
        }),
        [`${GroupsAction.unarchive}-warning`]: helperI18N('group-unarchive-success-some', {
            count,
            allCount
        }),
        [`${GroupsAction.resume}-warning`]: helperI18N('group-resume-success-some', {
            count,
            allCount
        }),
        [`${GroupsAction.stop}-warning`]: helperI18N('group-stop-success-some', {
            count,
            allCount
        }),
        [`${GroupsAction.delete}-error`]: helperI18N('group-delete-success-none'),
        [`${GroupsAction.delete}-success`]: helperI18N('group-delete-success'),
        [`${GroupsAction.delete}-warning`]: helperI18N('group-delete-success-some', {
            count,
            allCount
        }),
        [`${GroupsAction.sendBK}-success`]: helperI18N('group-sendBK-success'),
        [`${GroupsAction.sendBK}-error`]: helperI18N('group-sendBK-error'),
        [`${GroupsAction.archive}-error`]: helperI18N('group-archive-success-none'),
        [`${GroupsAction.unarchive}-error`]: helperI18N('group-unarchive-success-none'),
        [`${GroupsAction.resume}-error`]: helperI18N('group-resume-success-none'),
        [`${GroupsAction.stop}-error`]: helperI18N('group-stop-success-none'),
        [`${GroupsAction.remoderateBannersAdditions}-success`]: helperI18N(
            'group-remoderate-banners-additions-success'
        ),
        [`${GroupsAction.remoderateBannersAdditions}-error`]: helperI18N('group-remoderate-banners-additions-error'),
        [`${GroupsAction.remoderateBannersAdditions}-warning`]: helperI18N(
            'group-remoderate-banners-additions-success-some',
            {
                count,
                allCount
            }
        ),
        [`${GroupsAction.moderateAcceptBannersAdditions}-success`]: helperI18N(
            'group-moderate-accept-banners-additions-success'
        ),
        [`${GroupsAction.moderateAcceptBannersAdditions}-error`]: helperI18N(
            'group-moderate-accept-banners-additions-error'
        ),
        [`${GroupsAction.moderateAcceptBannersAdditions}-warning`]: helperI18N(
            'group-moderate-accept-banners-additions-success-some',
            {
                count,
                allCount
            }
        ),
        [`${GroupsAction.admSendMD}-success`]: helperI18N('group-adm-send-md-success'),
        [`${GroupsAction.admSendMD}-error`]: helperI18N('group-adm-send-md-error'),
        [`${GroupsAction.moderateAccept}-success`]: helperI18N('group-moderate-accept-success'),
        [`${GroupsAction.moderateAccept}-error`]: helperI18N('group-moderate-accept-error'),
        [`${GroupsAction.sendModerateByAgency}-success`]: helperI18N('group-send-moderate-success'),
        [`${GroupsAction.sendModerateByAgency}-error`]: helperI18N('group-send-moderate-error'),
        [`${GroupsAction.sendModerateByClient}-success`]: helperI18N('group-send-moderate-success'),
        [`${GroupsAction.sendModerateByClient}-error`]: helperI18N('group-send-moderate-error'),
        [`${GroupsAction.sendModerateByClient}-warning`]: helperI18N('group-send-moderate-success-some', {
            count,
            allCount
        })
    };

    return dictionary[`${action}-${noticeType}`];
}

export const showBannerBulkActionMessage = (bulkAction: GridAction, response: BannerBulkActionsResponse): void => {
    const { processedAdIds, skippedAdIds } = response;
    const noticeType = getNoticeTypeFromSuccessfulItemsRatioStrict(processedAdIds.length, skippedAdIds.length);

    const message = getBannersMassActionsResultMessage(
        bulkAction,
        processedAdIds.length,
        skippedAdIds.length,
        noticeType
    );

    showNoticeMessage(noticeType, message);
};

export const showBannersPartialUpdateMessage = (
    message: ReactNode,
    allBannerIds: string[],
    updatedBannerIds: string[],
    login: string,
    metrikaGoalToReach?: string
): void => {
    const updatedBannersCount = updatedBannerIds.length;
    const unsavedBannersCount = allBannerIds.length - updatedBannersCount;

    if (!updatedBannersCount) {
        showNoticeMessage(NoticeType.error, message);
    } else if (!unsavedBannersCount || unsavedBannersCount > 100) {
        showNoticeMessage(NoticeType.success, message);
    } else {
        const unsavedBanners = difference(allBannerIds, updatedBannerIds);

        showNoticeMessage(NoticeType.success, message, {
            customFields: {
                button: (
                    <ShowUnsavedBannersButton
                        login={login}
                        bannersIds={unsavedBanners}
                        metrikaGoalToReach={metrikaGoalToReach}
                    />
                )
            },
            timeout: 'none'
        });
    }
};

export function getBannersMassActionsResultMessage(
    action: GridAction,
    count: number,
    skippedCount: number,
    noticeType: NoticeType
): string {
    const allCount = skippedCount + count;

    const dictionary = {
        [`${BannersAction.archive}-success`]: helperI18N('banner-archive-success'),
        [`${BannersAction.unarchive}-success`]: helperI18N('banner-unarchive-success'),
        [`${BannersAction.resume}-success`]: helperI18N('banner-resume-success'),
        [`${BannersAction.stop}-success`]: helperI18N('banner-stop-success'),
        [`${BannersAction.delete}-success`]: helperI18N('banner-delete-success'),
        [`${BannersAction.archive}-warning`]: helperI18N('banner-archive-success-some', {
            count,
            allCount
        }),
        [`${BannersAction.unarchive}-warning`]: helperI18N('banner-unarchive-success-some', {
            count,
            allCount
        }),
        [`${BannersAction.resume}-warning`]: helperI18N('banner-resume-success-some', {
            count,
            allCount
        }),
        [`${BannersAction.stop}-warning`]: helperI18N('banner-stop-success-some', {
            count,
            allCount
        }),
        [`${BannersAction.delete}-warning`]: helperI18N('banner-delete-success-some', {
            count,
            allCount
        }),
        [`${BannersAction.archive}-error`]: helperI18N('banner-archive-success-none'),
        [`${BannersAction.unarchive}-error`]: helperI18N('banner-unarchive-success-none'),
        [`${BannersAction.resume}-error`]: helperI18N('banner-resume-success-none'),
        [`${BannersAction.stop}-error`]: helperI18N('banner-stop-success-none'),
        [`${BannersAction.delete}-error`]: helperI18N('banner-delete-success-none')
    };

    return dictionary[`${action}-${noticeType}`];
}

export function getPricesWizardSavedSuccessMessage(params: {
    phrasesCount: number;
    setCount: number;
    needObjectsOverLimitNotification: boolean;
}): string {
    if (params.setCount === 0) {
        return helperI18N('phrases-wizard-data-set-none');
    }
    if (params.needObjectsOverLimitNotification) {
        return helperI18N('phrases-wizard-data-set-success-with-yt-limit', {
            setCount: params.setCount
        });
    }

    return helperI18N('phrases-wizard-data-set-success', {
        setCount: params.setCount,
        phrasesCount: params.phrasesCount
    });
}

export function getBannersPricesSavedResultMessage(updatedBanners: number, unsavedBannersCount: number): string {
    if (unsavedBannersCount === 0) {
        return updatedBanners === 1 ? helperI18N('one-banners-saved') : helperI18N('all-banners-saved');
    }
    if (updatedBanners === 0) {
        return unsavedBannersCount === 1 ? helperI18N('one-banners-not-saved') : helperI18N('all-banners-not-saved');
    }

    return `${helperI18N('banners-success-saved', { count: updatedBanners })}. ${helperI18N('banners-fail-saved', {
        count: unsavedBannersCount
    })}`;
}

export function getBannersOrganizationSavedMessage(bannersCount: number, success: boolean): string {
    return success
        ? helperI18N('banners-organization-success-saved', {
              count: bannersCount
          })
        : helperI18N('banners-fail-saved', { count: bannersCount });
}

export function getBannersOrganizationSavedResultMessage(
    updatedBanners: number,
    unsavedBannersCount: number,
    errors: ValidationResultItem[] | null
): ReactNode {
    if (errors && errors.length) {
        const errorsOrganization = convertServerPathToClient(errors, organizationErrorPathMapper);
        const errorsOrganizationWithText: ValidationResultItem[] = mapTextToErrors(
            errorsOrganization,
            organizationErrorMapper
        );

        if (errorsOrganizationWithText[0] && errorsOrganizationWithText[0].text) {
            return errorsOrganizationWithText[0].text;
        }
    }

    if (unsavedBannersCount === 0) {
        return updatedBanners === 1 ? helperI18N('one-banners-saved') : helperI18N('all-banners-saved');
    }

    if (updatedBanners === 0) {
        // Для одного баннера показываем его ошибку, если есть
        if (unsavedBannersCount === 1 && errors && errors.length) {
            const errorsBanner = convertServerPathToClient(errors, organizationBannerErrorPathMapper);
            const errorsBannerWithText = mapTextToErrors(errorsBanner, organizationBannerErrorMapper);

            if (errorsBannerWithText[0] && errorsBannerWithText[0].text) {
                return errorsBannerWithText[0].text;
            }
        }

        return unsavedBannersCount === 1 ? helperI18N('one-banners-not-saved') : helperI18N('all-banners-not-saved');
    }

    return `${getBannersOrganizationSavedMessage(updatedBanners, true)}. ${getBannersOrganizationSavedMessage(
        unsavedBannersCount,
        false
    )}`;
}

export function isGroupActionAvailable(
    availableCount: number,
    actionFeatureType: keyof GroupItemActionAccess,
    selectionInfo: GridSelection,
    gridData: Array<Group>
): boolean {
    if (selectionInfo.length === 0) {
        return false;
    }

    const selectionInfoSet = new Set(selectionInfo);
    let countAvailableActionInGridData = 0;

    for (let i = 0; i < gridData.length; i++) {
        if (gridData[i] === undefined) {
            continue;
        }

        if (gridData[i].access[actionFeatureType]) {
            countAvailableActionInGridData++;
        }

        if (selectionInfoSet.has(gridData[i].id) && gridData[i].access[actionFeatureType]) {
            return true;
        }

        selectionInfoSet.delete(gridData[i].id);
    }

    return selectionInfoSet.size > 0 && availableCount - countAvailableActionInGridData > 0;
}

export function isSelectedMoreThenInGrid(selectionInfo: GridSelection, gridData: Array<GridRow>): boolean {
    const selectionInfoSet = new Set(selectionInfo);

    gridData.forEach(item => {
        if (item !== undefined) {
            selectionInfoSet.delete(item.id);
        }
    });

    return selectionInfoSet.size > 0;
}

// TODO: Рефакторинг - Дописать тексты ошибок для действий на всех гридах
export function getGridActionErrorMessage(action: GridAction, isMassAction: boolean): string {
    const dictionary = {
        [`${RetargetingsAction.resume}-false`]: helperI18N('retargetings-resume-error-one'),
        [`${RetargetingsAction.resume}-true`]: helperI18N('retargetings-resume-error-all'),
        [`${RetargetingsAction.suspend}-false`]: helperI18N('retargetings-suspend-error-one'),
        [`${RetargetingsAction.suspend}-true`]: helperI18N('retargetings-suspend-error-all'),
        [`${RetargetingsAction.setRetargetingCommonPriceEditor}-false`]: helperI18N(
            'retargetings-set-common-price-error-one'
        ),
        [`${RetargetingsAction.setRetargetingCommonPriceEditor}-true`]: helperI18N(
            'retargetings-set-common-price-error-all'
        ),
        [`${RetargetingsAction.delete}-false`]: helperI18N('retargetings-delete-error-one'),
        [`${RetargetingsAction.delete}-true`]: helperI18N('retargetings-delete-error-all'),
        [`${FeedFiltersAction.changeAutobudget}-false`]: helperI18N('feed-filters-change-priority-error-one'),
        [`${FeedFiltersAction.changeAutobudget}-true`]: helperI18N('feed-filters-change-priority-error-any'),
        [`${FeedFiltersAction.changeRate}-false`]: helperI18N('feed-filters-change-rate-error-one'),
        [`${FeedFiltersAction.changeRate}-true`]: helperI18N('feed-filters-change-rate-error-any'),
        [`${FeedFiltersAction.enable}-false`]: helperI18N('feed-filters-enable-error-one'),
        [`${FeedFiltersAction.enable}-true`]: helperI18N('feed-filters-enable-error-any'),
        [`${FeedFiltersAction.disable}-false`]: helperI18N('feed-filters-disable-error-one'),
        [`${FeedFiltersAction.disable}-true`]: helperI18N('feed-filters-disable-error-any'),
        [`${FeedFiltersAction.delete}-false`]: helperI18N('feed-filters-delete-error-one'),
        [`${FeedFiltersAction.delete}-true`]: helperI18N('feed-filters-delete-error-any'),
        [`${PhrasesAction.setPhrasesCommonPriceEditor}-false`]: helperI18N('phrases-set-common-price-error-one'),
        [`${PhrasesAction.setPhrasesCommonPriceEditor}-true`]: helperI18N('phrases-set-common-price-error-all')
    };

    return dictionary[`${action}-${isMassAction}`] || helperI18N('unknown-error');
}

export function showRetargetingActionNoticeMessage(action: GridAction, successCount: number, totalCount: number): void {
    const { noticeType, noticeMessageType } = getRequestResultNoticeParams(totalCount, successCount);

    const message = getRetargetingsActionNoticeMessage(action, successCount, totalCount, noticeMessageType);

    showNoticeMessage(noticeType, message);
}

export function getRetargetingsActionNoticeMessage(
    action: GridAction,
    count: number,
    totalCount: number,
    noticeType: NoticeType
): string {
    const isMassAction = totalCount !== 1;

    const dictionary = {
        [`${RetargetingsAction.resume}-${NoticeType.success}`]: isMassAction
            ? helperI18N('retargetings-resume-success-all')
            : helperI18N('retargetings-resume-success-one'),
        [`${RetargetingsAction.resume}-${NoticeType.warning}`]: helperI18N('retargetings-resume-warning-some', {
            count,
            totalCount
        }),
        [`${RetargetingsAction.resume}-${NoticeType.error}`]: getGridActionErrorMessage(
            RetargetingsAction.resume,
            isMassAction
        ),

        [`${RetargetingsAction.suspend}-${NoticeType.success}`]: isMassAction
            ? helperI18N('retargetings-suspend-success-all')
            : helperI18N('retargetings-suspend-success-one'),
        [`${RetargetingsAction.suspend}-${NoticeType.warning}`]: helperI18N('retargetings-suspend-warning-some', {
            count,
            totalCount
        }),
        [`${RetargetingsAction.suspend}-${NoticeType.error}`]: getGridActionErrorMessage(
            RetargetingsAction.suspend,
            isMassAction
        ),

        [`${RetargetingsAction.setRetargetingCommonPriceEditor}-${NoticeType.success}`]: isMassAction
            ? helperI18N('retargetings-set-common-price-success-all')
            : helperI18N('retargetings-set-common-price-success-one'),
        [`${RetargetingsAction.setRetargetingCommonPriceEditor}-${NoticeType.warning}`]: helperI18N(
            'retargetings-set-common-price-warning-some',
            {
                count,
                totalCount
            }
        ),
        [`${RetargetingsAction.setRetargetingCommonPriceEditor}-${NoticeType.error}`]: getGridActionErrorMessage(
            RetargetingsAction.setRetargetingCommonPriceEditor,
            isMassAction
        ),

        [`${RetargetingsAction.delete}-${NoticeType.success}`]: isMassAction
            ? helperI18N('retargetings-delete-success-all')
            : helperI18N('retargetings-delete-success-one'),
        [`${RetargetingsAction.delete}-${NoticeType.warning}`]: helperI18N('retargetings-delete-warning-some', {
            count,
            totalCount
        }),
        [`${RetargetingsAction.delete}-${NoticeType.error}`]: getGridActionErrorMessage(
            RetargetingsAction.delete,
            isMassAction
        )
    };

    return dictionary[`${action}-${noticeType}`];
}

export function showFeedFiltersActionNoticeMessage(action: GridAction, successCount: number, totalCount: number): void {
    const { noticeType, noticeMessageType } = getRequestResultNoticeParams(totalCount, successCount);

    const message = getFeedFiltersActionNoticeMessage(action, successCount, totalCount, noticeMessageType);

    showNoticeMessage(noticeType, message);
}

export function getFeedFiltersActionNoticeMessage(
    action: GridAction,
    successCount: number,
    totalCount: number,
    noticeType: NoticeType
): string {
    const isMassAction = totalCount !== 1;

    // change rate
    const changeRateSuccessMessage = isMassAction
        ? helperI18N('feed-filters-change-rate-success-any')
        : helperI18N('feed-filters-change-rate-success-one');

    const changeRateWarningMessage = helperI18N('feed-filters-change-rate-warning', {
        count: successCount,
        totalCount
    });

    // change priority
    const changePrioritySuccessMessage = isMassAction
        ? helperI18N('feed-filters-change-priority-success-any')
        : helperI18N('feed-filters-change-priority-success-one');

    const changePriorityWarningMessage = helperI18N('feed-filters-change-priority-warning', {
        count: successCount,
        totalCount
    });

    // enable
    const enableFeedFiltersSuccessMessage = isMassAction
        ? helperI18N('feed-filters-enable-success-any')
        : helperI18N('feed-filters-enable-success-one');
    const enableFeedFiltersWarningMessage = helperI18N('feed-filters-enable-warning', {
        count: successCount,
        totalCount
    });

    // disable
    const disableFeedFiltersSuccessMessage = isMassAction
        ? helperI18N('feed-filters-disable-success-any')
        : helperI18N('feed-filters-disable-success-one');
    const disableFeedFiltersWarningMessage = helperI18N('feed-filters-disable-warning', {
        count: successCount,
        totalCount
    });

    // delete
    const deleteFeedFiltersSuccessMessage = isMassAction
        ? helperI18N('feed-filters-delete-success-any')
        : helperI18N('feed-filters-delete-success-one');
    const deleteFeedFiltersWarningMessage = helperI18N('feed-filters-delete-warning', {
        count: successCount,
        totalCount
    });

    const dictionary = {
        // feed filters change rate
        [`${FeedFiltersAction.changeRate}-${NoticeType.success}`]: changeRateSuccessMessage,
        [`${FeedFiltersAction.changeRate}-${NoticeType.warning}`]: changeRateWarningMessage,
        [`${FeedFiltersAction.changeRate}-${NoticeType.error}`]: getGridActionErrorMessage(
            FeedFiltersAction.changeRate,
            isMassAction
        ),

        // feed filters change autobudget
        [`${FeedFiltersAction.changeAutobudget}-${NoticeType.success}`]: changePrioritySuccessMessage,
        [`${FeedFiltersAction.changeAutobudget}-${NoticeType.warning}`]: changePriorityWarningMessage,
        [`${FeedFiltersAction.changeAutobudget}-${NoticeType.error}`]: getGridActionErrorMessage(
            FeedFiltersAction.changeAutobudget,
            isMassAction
        ),

        // feed filters enable
        [`${FeedFiltersAction.enable}-${NoticeType.success}`]: enableFeedFiltersSuccessMessage,
        [`${FeedFiltersAction.enable}-${NoticeType.warning}`]: enableFeedFiltersWarningMessage,
        [`${FeedFiltersAction.enable}-${NoticeType.error}`]: getGridActionErrorMessage(
            FeedFiltersAction.enable,
            isMassAction
        ),

        // feed filters disable
        [`${FeedFiltersAction.disable}-${NoticeType.success}`]: disableFeedFiltersSuccessMessage,
        [`${FeedFiltersAction.disable}-${NoticeType.warning}`]: disableFeedFiltersWarningMessage,
        [`${FeedFiltersAction.disable}-${NoticeType.error}`]: getGridActionErrorMessage(
            FeedFiltersAction.disable,
            isMassAction
        ),

        // feed filters delete
        [`${FeedFiltersAction.delete}-${NoticeType.success}`]: deleteFeedFiltersSuccessMessage,
        [`${FeedFiltersAction.delete}-${NoticeType.warning}`]: deleteFeedFiltersWarningMessage,
        [`${FeedFiltersAction.delete}-${NoticeType.error}`]: getGridActionErrorMessage(
            FeedFiltersAction.delete,
            isMassAction
        )
    };

    if (isUndefined(dictionary[`${action}-${noticeType}`])) {
        throw new Error(`Нет текста сообщения для действия ${action} типа ${noticeType}`);
    }

    return dictionary[`${action}-${noticeType}`];
}

export function showRequestResultNoticeMessage(allRequestItems: number, successItems: number, message: string): void {
    const { noticeType } = getRequestResultNoticeParams(allRequestItems, successItems);

    showNoticeMessage(noticeType, message);
}
